import React from 'react'
import { FaUser, FaChild } from 'react-icons/fa'
import { getIdFormatHint } from '../utils/validators'

const ID_TYPE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'passport', label: 'Passport' },
  { value: 'driving_licence', label: 'Driving Licence' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'pan', label: 'PAN Card' },
]

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
]

const GuestDetailsForm = ({ guests, onChange, errors }) => {
  const handleGuestChange = (index, field, value) => {
    const updated = guests.map((g, i) => (i === index ? { ...g, [field]: value } : g))
    onChange(updated)
  }

  if (!guests || guests.length === 0) return null

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <FaUser className="text-blue-600" />
        Guest Details
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Please provide details for all {guests.length} traveller{guests.length > 1 ? 's' : ''}.
      </p>

      <div className="space-y-6">
        {guests.map((guest, index) => {
          const guestErrors = errors?.[index] || {}
          const isChild = guest.is_child

          return (
            <div
              key={`guest-${index}-${isChild ? 'child' : 'adult'}`}
              className={`border rounded-lg p-4 ${isChild ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}
            >
              <div className="flex items-center gap-2 mb-3">
                {isChild ? (
                  <FaChild className="text-green-600" />
                ) : (
                  <FaUser className="text-blue-600" />
                )}
                <span className="font-semibold text-gray-800">
                  {isChild
                    ? `Child ${index - guests.filter((g, i) => i < index && !g.is_child).length + 1}`
                    : `Adult ${guests.filter((g, i) => i <= index && !g.is_child).length}`}
                </span>
                {index === 0 && !isChild && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    Primary
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Full Name */}
                <div>
                  <label
                    htmlFor={`guest-name-${index}`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`guest-name-${index}`}
                    type="text"
                    value={guest.full_name}
                    onChange={(e) => handleGuestChange(index, 'full_name', e.target.value)}
                    maxLength={100}
                    placeholder="Enter full name"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                      guestErrors.full_name ? 'border-red-400' : 'border-gray-300'
                    }`}
                  />
                  {guestErrors.full_name && (
                    <p className="text-red-500 text-xs mt-1">{guestErrors.full_name}</p>
                  )}
                </div>

                {/* Age */}
                <div>
                  <label
                    htmlFor={`guest-age-${index}`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`guest-age-${index}`}
                    type="number"
                    value={guest.age}
                    onChange={(e) => handleGuestChange(index, 'age', e.target.value)}
                    min={0}
                    max={120}
                    placeholder="Age"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                      guestErrors.age ? 'border-red-400' : 'border-gray-300'
                    }`}
                  />
                  {guestErrors.age && (
                    <p className="text-red-500 text-xs mt-1">{guestErrors.age}</p>
                  )}
                </div>

                {/* Gender */}
                <div>
                  <label
                    htmlFor={`guest-gender-${index}`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    id={`guest-gender-${index}`}
                    value={guest.gender}
                    onChange={(e) => handleGuestChange(index, 'gender', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                      guestErrors.gender ? 'border-red-400' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select</option>
                    {GENDER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {guestErrors.gender && (
                    <p className="text-red-500 text-xs mt-1">{guestErrors.gender}</p>
                  )}
                </div>

                {/* ID Type */}
                <div>
                  <label
                    htmlFor={`guest-idtype-${index}`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    ID Type
                  </label>
                  <select
                    id={`guest-idtype-${index}`}
                    value={guest.id_type}
                    onChange={(e) => handleGuestChange(index, 'id_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {ID_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ID Number */}
                {guest.id_type && guest.id_type !== 'none' && (
                  <div>
                    <label
                      htmlFor={`guest-idnum-${index}`}
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      ID Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      id={`guest-idnum-${index}`}
                      type="text"
                      value={guest.id_number}
                      onChange={(e) => handleGuestChange(index, 'id_number', e.target.value)}
                      maxLength={50}
                      placeholder={getIdFormatHint(guest.id_type) || 'Enter ID number'}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                        guestErrors.id_number ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                    {guestErrors.id_number && (
                      <p className="text-red-500 text-xs mt-1">{guestErrors.id_number}</p>
                    )}
                    {!guestErrors.id_number && getIdFormatHint(guest.id_type) && (
                      <p className="text-gray-400 text-xs mt-1">{getIdFormatHint(guest.id_type)}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default GuestDetailsForm
